// src/components/charts/PlatformByFormatBar.jsx
//
// Stacked horizontal bar: one bar per platform, segmented by content_format.
// Answers "which platform, and with which content" in a single view.
//
// Sample-based: the stats endpoint only supports a single groupBy dimension
// (status | platform | content_format | day), so a platform×format cross-tab
// is derived in the browser from the run sample rows (last 200), NOT /runs/stats.
//
import React from 'react';
import { CHART_SERIES, COLORS, FONT_SANS } from '../../constants/theme.js';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';

const NO_PLATFORM = 'Senza piattaforma';
const NO_FORMAT = 'Senza formato';

// Series colours come from the shared categorical palette, assigned in fixed
// order so a format keeps its colour when the filters change how many formats
// are present. Formats past slot 8 fold into the last slot rather than cycling
// back onto colours already in use.
const PALETTE = CHART_SERIES;

/**
 * Cross-tabulate run rows by platform (bar) × content_format (stack segment).
 * Returns { rows, formats }:
 *   - rows: [{ platform, total, [format]: count, ... }] sorted by total desc
 *   - formats: ordered list of content_format keys present (by total desc)
 * Null/empty platform → "Senza piattaforma"; null/empty format → "Senza formato".
 * Exported for unit testing.
 */
export function platformByFormatRows(runs) {
  if (!Array.isArray(runs) || runs.length === 0) return { rows: [], formats: [] };

  const byPlatform = new Map();   // platform -> Map(format -> count)
  const formatTotals = new Map(); // format -> count

  for (const r of runs) {
    const platform = r?.platform == null || r.platform === '' ? NO_PLATFORM : r.platform;
    const format = r?.content_format == null || r.content_format === '' ? NO_FORMAT : r.content_format;
    if (!byPlatform.has(platform)) byPlatform.set(platform, new Map());
    const fmtMap = byPlatform.get(platform);
    fmtMap.set(format, (fmtMap.get(format) || 0) + 1);
    formatTotals.set(format, (formatTotals.get(format) || 0) + 1);
  }

  const formats = [...formatTotals.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([fmt]) => fmt);

  const rows = [...byPlatform.entries()]
    .map(([platform, fmtMap]) => {
      const row = { platform, total: 0 };
      for (const fmt of formats) {
        const c = fmtMap.get(fmt) || 0;
        row[fmt] = c;
        row.total += c;
      }
      return row;
    })
    .sort((a, b) => b.total - a.total);

  return { rows, formats };
}

export default function PlatformByFormatBar({ runs }) {
  const { rows, formats } = platformByFormatRows(runs);
  if (rows.length === 0) return null;

  return (
    <ResponsiveContainer width="100%" height={Math.max(200, rows.length * 36 + 60)}>
      <BarChart data={rows} layout="vertical" margin={{ top: 8, right: 24, bottom: 8, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" horizontal={false} />
        <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: COLORS.textSecondary }} />
        <YAxis type="category" dataKey="platform" width={120} tick={{ fontSize: 11, fill: COLORS.ink }} />
        <Tooltip contentStyle={{ fontSize: 12, fontFamily: FONT_SANS }} />
        <Legend wrapperStyle={{ fontSize: 11, fontFamily: FONT_SANS }} />
        {formats.map((fmt, i) => (
          <Bar key={fmt} dataKey={fmt} stackId="fmt" fill={PALETTE[Math.min(i, PALETTE.length - 1)]} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}
